import { createApp } from "vue";
import App from "./App.vue";
import router from "./router";
import pinia from "./stores";
import vuetify from "./plugins/vuetify";
import eventBus from "./eventBus";
import axios from "axios";
import Vapor from "laravel-vapor";
import moment from "moment";
import * as Sentry from "@sentry/vue";
import { redirectToLogin } from "./stores/helpers";
import "./sass/app.scss";

window.Vapor = Vapor;
window.moment = moment;

window.axios = axios;
window.axios.defaults.baseURL = import.meta.env.VITE_BASE_API_URL;
window.axios.defaults.headers.common["X-Requested-With"] = "XMLHttpRequest";
window.axios.defaults.withCredentials = true;
window.axios.defaults.withXSRFToken = true;

let isLoggingOut = false;

window.axios.interceptors.response.use(
    (response) => {
        return response;
    },
    function (error) {
        if (error.response?.status == 401 || error.response?.status == 419) {
            let requiresAuth = error.response?.status == 419 || error.response?.data?.message?.includes("Unauthenticated.");

            if (requiresAuth && !isLoggingOut) {
                isLoggingOut = true;
                localStorage.removeItem('user');

                eventBus.emit("SHOW_ALERT", {
                    status: 401,
                    data: {
                        message: `Your session has expired.`,
                    },
                    statusText: "",
                });

                redirectToLogin();
                return Promise.reject(error);
            }
        }
        return Promise.reject(error);
    }
);

// Clean up old vuex-persist data
localStorage.removeItem('vuex');

const app = createApp(App);

Sentry.init({
    app,
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_ENV || import.meta.env.MODE,
    ignoreErrors: [
        "Non-Error exception captured",
        "Non-Error promise rejection captured",
        "Navigation cancelled from",
    ],
    integrations: [
        Sentry.browserTracingIntegration({ router }),
        Sentry.replayIntegration({
            maskAllText: false,
            maskAllInputs: false,
            mask: ['input[type="password"]'],
            blockAllMedia: false,
        }),
        Sentry.feedbackIntegration({
            colorScheme: "dark",
            autoInject: false,
            showBranding: false,
            themeDark: {
                foreground: "#E6E6E6",
                background: "#363636",
            },
            triggerLabel: "Need help?",
            formTitle: "Send Feedback",
            messageLabel: "Feedback",
            messagePlaceholder: "Describe your issue or suggestion",
            submitButtonLabel: "Send Feedback",
            successMessageText: "Thank you for your feedback!",
        }),
    ],
    tracesSampleRate: 0.2,
    tracePropagationTargets: ["localhost", /^https:\/\/(api|app)\.fluxoutdoor\.com/],
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
});

app.use(pinia);
app.use(router);
app.use(vuetify);

// Custom v-clipboard directive (replaces vue-clipboard2 directives)
app.directive('clipboard', {
    mounted(el, binding) {
        if (binding.arg === 'copy') {
            el._clipboardValue = binding.value;
        }
        if (binding.arg === 'success') {
            el._clipboardSuccess = binding.value;
        }
        if (!el._clipboardHandler) {
            el._clipboardHandler = () => {
                const text = el._clipboardValue;
                if (text !== undefined && text !== null) {
                    navigator.clipboard.writeText(String(text)).then(() => {
                        if (typeof el._clipboardSuccess === 'function') {
                            el._clipboardSuccess({ text: String(text) });
                        }
                    }).catch((err) => {
                        console.warn('Clipboard copy failed:', err);
                    });
                }
            };
            el.addEventListener('click', el._clipboardHandler);
        }
    },
    updated(el, binding) {
        if (binding.arg === 'copy') {
            el._clipboardValue = binding.value;
        }
        if (binding.arg === 'success') {
            el._clipboardSuccess = binding.value;
        }
    },
    unmounted(el) {
        if (el._clipboardHandler) {
            el.removeEventListener('click', el._clipboardHandler);
        }
    },
});

app.mount("#app");
